import { acceptTreeShare, declineTreeShare } from "api/sharing";
import { getSubscriptionStatus } from "api/subscriptions";
import { getSharedWithMe } from "api/trees";
import type { CollaboratorAvatarDto, SharedTreeDto } from "api/types";
import { UserAvatar } from "components/UserAvatar";
import { HomeSidebar } from "pages/HomePage/components";
import LineIcon from "pages/HomePage/components/LineIcon";
import { defaultCoverPreview } from "pages/HomePage/data";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import Paths, { getTreeViewPath } from "routes/Paths";
import { useSession } from "Session";
import { formatLastModified } from "utilities";

import styles from "./SharedWithMePage.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

/** Trees other people own that were shared with the current user, with their role on each. */
const SharedWithMePage = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { getAccessToken, signOut, user } = useSession();
	const [trees, setTrees] = useState<SharedTreeDto[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isPro, setIsPro] = useState(false);
	const [busyTreeId, setBusyTreeId] = useState<string | null>(null);

	const displayName = user?.fullName ?? t("common:yourAccount");
	const initials = getInitials(user?.fullName ?? "");

	useEffect(() => {
		let isCurrent = true;

		const load = async (): Promise<void> => {
			const token = await getAccessToken();
			if (!token || !isCurrent) {
				return;
			}

			// The Pro badge is cosmetic here; ignore failures.
			const [shared, subscription] = await Promise.all([
				getSharedWithMe(token),
				getSubscriptionStatus(token).catch(() => null)
			]);

			if (!isCurrent) {
				return;
			}

			setTrees(shared);
			setIsPro(subscription?.isPro ?? false);
		};

		load()
			.catch(() => undefined)
			.finally(() => {
				if (isCurrent) {
					setIsLoading(false);
				}
			});

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken]);

	const onUpgrade = useCallback((): void => navigate(Paths.plans), [navigate]);

	const onAcceptShare = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
			const treeId = event.currentTarget.dataset.treeId;
			if (!treeId || busyTreeId) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setBusyTreeId(treeId);
			try {
				await acceptTreeShare(treeId, token);
				setTrees((current: SharedTreeDto[]) =>
					current.map((tree: SharedTreeDto) => (tree.id === treeId ? { ...tree, myStatus: "Active" } : tree))
				);
			} catch {
				// Keep the card pending so the user can retry.
			} finally {
				setBusyTreeId(null);
			}
		},
		[busyTreeId, getAccessToken]
	);

	const onRejectShare = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
			const treeId = event.currentTarget.dataset.treeId;
			if (!treeId || busyTreeId) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setBusyTreeId(treeId);
			try {
				await declineTreeShare(treeId, token);
				setTrees((current: SharedTreeDto[]) => current.filter((tree: SharedTreeDto) => tree.id !== treeId));
			} catch {
				// Keep the card so the user can retry.
			} finally {
				setBusyTreeId(null);
			}
		},
		[busyTreeId, getAccessToken]
	);

	return (
		<div className={styles.shell}>
			<HomeSidebar
				displayName={displayName}
				initials={initials}
				isPro={isPro}
				onSignOut={signOut}
				onUpgrade={onUpgrade}
				profileImageUrl={user?.profileImageUrl}
			/>

			<main className={styles.main}>
				<header className={styles.header}>
					<h1 className={styles.heading}>{t("sharedPage.title")}</h1>
					<p className={styles.subheading}>{t("sharedPage.subtitle")}</p>
				</header>

				{isLoading && (
					<div className={styles.emptyState}>
						<p>{t("sharedPage.loading")}</p>
					</div>
				)}

				{!isLoading && trees.length === 0 && (
					<div className={styles.emptyState}>
						<p>{t("sharedPage.empty")}</p>
					</div>
				)}

				{trees.length > 0 && (
					<section aria-label={t("sharedPage.title")} className={styles.treeGrid}>
						{trees.map((tree: SharedTreeDto) => {
							const isPending = tree.myStatus === "Pending";
							const content = (
								<>
									<div className={styles.coverFrame}>
										<img
											alt=""
											className={styles.coverImage}
											src={tree.coverImageUrl?.trim() || defaultCoverPreview}
										/>
										<span className={styles.roleBadge}>{tree.myRole}</span>
									</div>
									<div className={styles.cardBody}>
										<h2 className={styles.cardTitle}>{tree.title}</h2>
										<p className={styles.cardDescription}>
											{tree.description?.trim() || t("library.noDescription")}
										</p>
										<div className={styles.cardMeta}>
											<div className={styles.metaRow}>
												<span className={styles.ownerLine}>
													<span className={styles.avatarStack}>
														{(tree.collaborators?.length > 0
															? tree.collaborators.slice(0, 4)
															: [
																	{
																		fullName: tree.ownerName,
																		profileImageUrl: tree.ownerProfileImageUrl
																	}
															  ]
														).map((collaborator: CollaboratorAvatarDto) => (
															<UserAvatar
																key={`${tree.id}-${collaborator.fullName}`}
																className={styles.ownerAvatar}
																imageUrl={collaborator.profileImageUrl}
																initials={getInitials(collaborator.fullName)}
																title={collaborator.fullName}
															/>
														))}
													</span>
													{t("sharedPage.byOwner", { name: tree.ownerName })}
												</span>
												<span
													className={
														tree.isLikedByMe
															? `${styles.likeCount} ${styles.likeCountLiked}`
															: styles.likeCount
													}>
													<LineIcon name="heart" />
													{tree.likeCount}
												</span>
											</div>
											{/* Entity count then the edited/pending line, matching the footer row on
											    the library and explore cards. */}
											<div className={styles.metaRow}>
												<span className={styles.entityCount}>
													<LineIcon name="tree" />
													<span>{t("library.entityCount", { count: tree.entityCount })}</span>
												</span>
												<span className={styles.editedAt}>
													{isPending
														? t("sharedPage.pendingHint")
														: t("library.lastEdited", {
																value: formatLastModified(tree.lastModifiedDate)
														  })}
												</span>
											</div>
										</div>
									</div>
								</>
							);

							return (
								<article key={tree.id} className={styles.treeCard}>
									{isPending ? (
										<div className={styles.treeCardStatic}>
											{content}
											<div className={styles.pendingActions}>
												<button
													className={styles.acceptButton}
													data-tree-id={tree.id}
													disabled={busyTreeId === tree.id}
													onClick={onAcceptShare}
													type="button">
													{t("sharedPage.accept")}
												</button>
												<button
													className={styles.rejectButton}
													data-tree-id={tree.id}
													disabled={busyTreeId === tree.id}
													onClick={onRejectShare}
													type="button">
													{t("sharedPage.reject")}
												</button>
											</div>
										</div>
									) : (
										<Link className={styles.treeCardLink} to={getTreeViewPath(tree.id)}>
											{content}
										</Link>
									)}
								</article>
							);
						})}
					</section>
				)}
			</main>
		</div>
	);
};

export default SharedWithMePage;
